// PidPLUModal.jsx — Add/Edit surface for PID PLU on the shared PidModal scaffold.
// One body, two modes (Create/Edit identical by construction — plan decision 2):
// Commodity picker, Label ID, Description, GS1 DataBar (12-digit UPC-A validation),
// Size Options + Import Sizes multi-selects (scoped to the commodity, cleared on
// commodity change), single staged image, and — in edit mode — the existing-image
// grid with immediate delete plus Delete PLU (ConfirmDelete).
// Save flows preserved from the retired panels: create POSTs /api/pidplu then uploads
// the image against the inserted id; edit PUTs the spread row (all fields, as before)
// then uploads.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactSelect from 'react-select';
import { Trash2 } from 'lucide-react';
import authAxios from '../utils/authAxios';
import PidModal, {
  PrimaryButton, QuietButton,
  MSection, MSectionTitle, MSectionNote,
  FieldGrid, FieldWrap, FieldLabel, FieldInput, FieldError,
} from './PidModal';
import { ConfirmDelete } from '../styles/TableStyles';
import {
  customSelectStyles,
  SizeTagWrapper,
  SizeTag,
} from '../styles/PackageIndexStyles';
import {
  digitsOnly,
  validateUPC12,
  preventNonDigitsOnKeyDown,
  handlePasteDigitsOnly,
} from '../utils/barcodeUtils';

const FileInput = styled.input`
  font: inherit;
`;

const ImageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ImagePreview = styled.div`
  position: relative;
`;

const PreviewImg = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 1px 4px var(--shadow-a15);
`;

const DeleteBtn = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  background: var(--white);
  border: none;
  border-radius: 50%;
  padding: 3px;
  cursor: pointer;

  &:hover { background: var(--color-danger-soft); }
`;

const CDN_BASE = 'https://thevaultmedia.azureedge.net/vault-images';
const PLU_FOLDER = 'PIDPLU';
const FALLBACK = 'https://thevaultmedia.blob.core.windows.net/vault-images/PIDPLU/PLUFallback.webp';

function buildCdnUrl(filename) {
  const clean = String(filename || '').trim().replace(/^\/+/, '');
  return `${CDN_BASE}/${PLU_FOLDER}/${encodeURIComponent(clean)}`;
}

const PidPLUModal = ({ mode, item, onClose, onSave }) => {
  const isEdit = mode === 'edit';

  const [formData, setFormData] = useState(() => (isEdit
    ? { ...item }
    : { CommodityUniqueID: '', LabelID: '', Description: '', GS1DataBar: '' }));
  const [commodities, setCommodities] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedImportSizes, setSelectedImportSizes] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [existingImages, setExistingImages] = useState([]); // filenames only (edit)
  const [saving, setSaving] = useState(false);
  const [gs1Error, setGs1Error] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const requests = [
          authAxios.get('/api/commodities'),
          authAxios.get('/api/sizeoptions/dropdown?origin=Domestic'),
        ];
        if (isEdit) requests.push(authAxios.get(`/api/pidplu/images?pluId=${item.UniqueID}`));
        const [commoditiesRes, sizesRes, imagesRes] = await Promise.all(requests);

        setCommodities((commoditiesRes.data || []).map(c => ({
          value: Number(c.UniqueID), label: c.Commodity,
        })));

        const sizeOpts = (sizesRes.data || []).map(s => ({
          value: String(s.UniqueID),
          label: s.SizeOption,
          CommodityUniqueID: Number(s.CommodityUniqueID),
        }));
        setSizeOptions(sizeOpts);

        if (isEdit) {
          setSelectedSizes(
            (item.SizeOptionIDs || '').split(',').map(id => sizeOpts.find(o => o.value === id)).filter(Boolean)
          );
          setSelectedImportSizes(
            (item.ImportSizeIDs || '').split(',').map(id => sizeOpts.find(o => o.value === id)).filter(Boolean)
          );
          setExistingImages(Array.isArray(imagesRes?.data) ? imagesRes.data : []);
        }
      } catch (err) {
        console.error('[PLU dropdown load failed]', err);
      }
    })();
  }, [isEdit, item]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'GS1DataBar') {
      const d = digitsOnly(value);
      setFormData(prev => ({ ...prev, [name]: d }));
      let err = '';
      if (d.length > 0) {
        if (d.length !== 12) err = 'GS1-12 (UPC-A) must be 12 digits';
        else if (!validateUPC12(d)) err = 'Invalid UPC-A check digit';
      }
      setGs1Error(err);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const uploadImage = async (pluId) => {
    if (!imageFile || !pluId) return null;
    const okTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!okTypes.includes(imageFile.type)) {
      alert('Please upload PNG, JPG, or WEBP.');
      return null;
    }
    const sanitizedLabel = String(formData.LabelID || '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9-_]/g, '');

    const form = new FormData();
    form.append('image', imageFile);
    form.append('name', sanitizedLabel || 'PLU');

    const up = await authAxios.post(`/uploadImage/${PLU_FOLDER}`, form);
    const blobName = up?.data?.blobName || '';
    const savedFileName = blobName.split('/').pop();

    if (savedFileName) {
      await authAxios.post('/api/pidplu/images', {
        PLUUniqueID: pluId,
        ImagePath: savedFileName,
      });
    }
    return savedFileName;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (formData.GS1DataBar) {
        const d = digitsOnly(formData.GS1DataBar);
        if (!(d.length === 12 && validateUPC12(d))) {
          alert('GS1-12 is invalid (UPC-A)');
          return;
        }
      }
      const sizeCsv = selectedSizes.map(s => s.value).join(',');
      const importCsv = selectedImportSizes.map(s => s.value).join(',');

      if (isEdit) {
        await authAxios.put(`/api/pidplu/${item.UniqueID}`, {
          ...formData,
          GS1DataBar: digitsOnly(formData.GS1DataBar),
          SizeOptionIDs: sizeCsv,
          ImportSizeIDs: importCsv,
        });
        await uploadImage(item.UniqueID);
      } else {
        const payload = {
          ...formData,
          CommodityUniqueID: Number(formData.CommodityUniqueID) || null,
          SizeOptionIDs: sizeCsv,
          ImportSizeIDs: importCsv,
          GS1DataBar: digitsOnly(formData.GS1DataBar),
        };
        const res = await authAxios.post('/api/pidplu', payload);
        const insertedId = res.data.insertedId || res.data.UniqueID;
        await uploadImage(insertedId);
      }

      onSave();
    } catch (err) {
      console.error('[PLU Save Error]', err);
      alert(isEdit ? 'Failed to save PLU changes.' : 'Failed to save PLU');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImage = async (filename) => {
    if (!filename || /PLUFallback\.png$/i.test(filename)) return;
    try {
      await authAxios.delete(`/api/pidplu/images/${encodeURIComponent(filename)}`);
      setExistingImages(prev => prev.filter(f => f !== filename));
    } catch (err) {
      console.error('[Delete PLU image failed]', err);
      alert('Failed to delete image');
    }
  };

  const handleDeletePLU = async () => {
    setSaving(true);
    try {
      await authAxios.delete(`/api/pidplu/${item.UniqueID}`);
      onSave(); // parent refreshes and closes
    } catch (err) {
      console.error('[Delete PLU Error]', err);
      alert('Failed to delete PLU');
    } finally {
      setSaving(false);
    }
  };

  const filteredSizeOptions = useMemo(
    () => sizeOptions.filter(s => Number(s.CommodityUniqueID) === Number(formData.CommodityUniqueID)),
    [sizeOptions, formData.CommodityUniqueID]
  );

  return (
    <PidModal
      title={isEdit ? `Edit PID PLU — ${item?.LabelID || ''}` : 'New PID PLU'}
      status={isEdit ? item?.Status : 'Pending'}
      onClose={onClose}
      footerLeft={isEdit ? (
        <ConfirmDelete onConfirm={handleDeletePLU} label="Delete" disabled={saving} />
      ) : null}
      footerRight={(
        <>
          <QuietButton type="button" onClick={onClose} disabled={saving}>Cancel</QuietButton>
          <PrimaryButton type="button" onClick={handleSave} disabled={saving || Boolean(gs1Error)}>Save</PrimaryButton>
        </>
      )}
    >
      <MSection>
        <MSectionTitle>PLU Details</MSectionTitle>
        <FieldGrid>
          <FieldWrap>
            <FieldLabel>Commodity</FieldLabel>
            <ReactSelect
              options={commodities}
              styles={customSelectStyles}
              value={commodities.find(c => c.value === Number(formData.CommodityUniqueID)) || null}
              onChange={(opt) => {
                setFormData(prev => ({ ...prev, CommodityUniqueID: opt?.value ?? null }));
                setSelectedSizes([]);
                setSelectedImportSizes([]);
              }}
            />
          </FieldWrap>
          <FieldWrap>
            <FieldLabel htmlFor="plu-LabelID">Label ID</FieldLabel>
            <FieldInput id="plu-LabelID" name="LabelID" value={formData.LabelID || ''} onChange={handleChange} />
          </FieldWrap>
          <FieldWrap>
            <FieldLabel htmlFor="plu-Description">Description</FieldLabel>
            <FieldInput id="plu-Description" name="Description" value={formData.Description || ''} onChange={handleChange} />
          </FieldWrap>
          <FieldWrap>
            <FieldLabel htmlFor="plu-GS1DataBar">GS1 Data Bar</FieldLabel>
            <FieldInput
              id="plu-GS1DataBar"
              name="GS1DataBar"
              value={formData.GS1DataBar || ''}
              onChange={handleChange}
              onKeyDown={preventNonDigitsOnKeyDown}
              onPaste={(e) => handlePasteDigitsOnly(e, (d) => setFormData(prev => ({ ...prev, GS1DataBar: d })))}
              placeholder="12 digits (UPC-A)"
              inputMode="numeric"
            />
            {gs1Error && <FieldError>{gs1Error}</FieldError>}
          </FieldWrap>
        </FieldGrid>
      </MSection>

      <MSection>
        <MSectionTitle>Size Options</MSectionTitle>
        <ReactSelect
          isMulti
          options={filteredSizeOptions}
          styles={customSelectStyles}
          value={selectedSizes}
          onChange={setSelectedSizes}
        />
        {selectedSizes.length > 0 && (
          <SizeTagWrapper style={{ marginTop: '0.5rem', justifyContent: 'flex-start' }}>
            {selectedSizes.map((s, i) => (
              <SizeTag key={`size-${i}`} style={{ background: 'var(--green-600)', color: 'var(--white)' }}>
                {s.label}
              </SizeTag>
            ))}
          </SizeTagWrapper>
        )}
      </MSection>

      <MSection>
        <MSectionTitle>Import Sizes</MSectionTitle>
        <ReactSelect
          isMulti
          options={filteredSizeOptions}
          styles={customSelectStyles}
          value={selectedImportSizes}
          onChange={setSelectedImportSizes}
        />
        {selectedImportSizes.length > 0 && (
          <SizeTagWrapper style={{ marginTop: '0.5rem', justifyContent: 'flex-start' }}>
            {selectedImportSizes.map((s, i) => (
              <SizeTag key={`import-${i}`} style={{ background: 'var(--grey-700)', color: 'var(--white)' }}>
                {s.label}
              </SizeTag>
            ))}
          </SizeTagWrapper>
        )}
      </MSection>

      <MSection>
        <MSectionTitle>Images</MSectionTitle>
        {isEdit && (
          <ImageGrid style={{ marginBottom: 8 }}>
            {existingImages.length > 0 ? (
              existingImages.map((filename, idx) => (
                <ImagePreview key={idx}>
                  <PreviewImg src={buildCdnUrl(filename)} alt={filename} />
                  <DeleteBtn onClick={() => handleDeleteImage(filename)} title="Delete Image">
                    <Trash2 size={16} />
                  </DeleteBtn>
                </ImagePreview>
              ))
            ) : (
              <PreviewImg src={FALLBACK} alt="Fallback" />
            )}
          </ImageGrid>
        )}
        <FileInput
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          onChange={(e) => setImageFile(e.target.files?.[0] || null)}
        />
        {imageFile && <MSectionNote style={{ marginTop: 6 }}>Uploads when you click Save.</MSectionNote>}
      </MSection>
    </PidModal>
  );
};

export default PidPLUModal;
